// Over / Under Rates — split bars per goal line, one team (Phase 3).
//
// Five goal lines (0.5–4.5); each is a split bar — a green "over" fill + a red
// "under" fill summing to 100%. `.ou-over-fill`/`.ou-under-fill` have CSS widths
// (0% / 100%) and NO transition, so — like StatPairBars — React owns the shell
// (labels + value text) and **D3 owns the fill widths** in a useEffect (verbatim
// port of the legacy d3 width transition). The shell is keyed by fixture+team so
// changing either remounts it (fills reset, then animate) — matching the legacy
// cacheKey rebuild. Fixture-first; team + timeline from the bridge context.

import { useEffect, useRef } from "react";
import { select } from "d3-selection";
import "d3-transition";
import { easeCubicInOut } from "d3-ease";
import { shapeOverUnder, type OverUnderLine } from "@bento/shared";
import { useWidgetData } from "../../data/useWidgetData.js";
import { useFixtureKey } from "../../data/useFixtureKey.js";
import WidgetSkeleton from "./WidgetSkeleton.js";
import WidgetError from "./WidgetError.js";

const DURATION = 300;

export interface OverUnderProps {
  fixture: string;
  /** Selected team (full name); falls back to the fixture's home team. */
  team?: string | null;
  /** Timeline toggle: "5" | "10" | "Season"; defaults to "5". */
  timeline?: string | null;
}

export default function OverUnder({ fixture, team, timeline }: OverUnderProps) {
  const home = (fixture || "Arsenal v Everton").split(" v ")[0]?.trim() ?? "";
  const selected = team || home;
  // Data now from app-api (was the `getOverUnderData` legacyData getter); the
  // shaper + D3-driven fills below are unchanged. Controls reshape client-side.
  const { data, isPending, isError, refetch } = useWidgetData(
    "over-under-rates",
    useFixtureKey(fixture)
  );
  const view = shapeOverUnder(data ?? {}, selected, timeline || "5");

  // Hooks must run unconditionally (before the isPending/isError early returns),
  // so `useRef`/`useEffect` stay here — while pending `lines` is empty and the
  // effect is a no-op, then re-runs once the loaded wrapper mounts.
  const wrapRef = useRef<HTMLDivElement>(null);
  const lines: OverUnderLine[] = view?.lines ?? [];
  const sig = `${fixture}|${selected}|${lines.map((l) => l.pct).join(",")}`;

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    lines.forEach((l) => {
      const over = wrap.querySelector<HTMLElement>(
        `.ou-over-fill[data-line="${l.key}"]`
      );
      const row = over?.closest(".ou-row");
      const under = row?.querySelector<HTMLElement>(".ou-under-fill");
      if (over)
        select(over)
          .transition()
          .duration(DURATION)
          .ease(easeCubicInOut)
          .style("width", l.pct + "%");
      if (under)
        select(under)
          .transition()
          .duration(DURATION)
          .ease(easeCubicInOut)
          .style("width", 100 - l.pct + "%")
          // The 3px gap between the two segments only exists when both show —
          // at 0% / 100% the remaining segment runs edge to edge.
          .style("margin-left", l.pct > 0 && l.pct < 100 ? "3px" : "0px");
    });
    // `sig` covers fixture + team + every line's pct — the complete dependency.
  }, [sig]);

  if (isPending) return <WidgetSkeleton />;
  if (isError) return <WidgetError onRetry={refetch} />;

  if (!view) {
    return (
      <div className="data-wrapper top-align">
        <div className="module-empty">No data</div>
      </div>
    );
  }

  return (
    <div className="data-wrapper top-align" ref={wrapRef}>
      <div className="ou-block" key={`${fixture}|${selected}`}>
        <div className="sc-team-label">{view.team}</div>
        {view.lines.map((l) => (
          <div className="ou-row" key={l.key}>
            <div className="sc-stat-header">
              <span className="sc-stat-name">{l.label}</span>
              <span className="sc-stat-val ou-val">{l.pct}%</span>
            </div>
            <div className="ou-bar-bg">
              <div className="ou-over-fill bento-fill" data-line={l.key} />
              <div className="ou-under-fill bento-fill" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
